import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { toast } from 'react-toastify';
import { useAuth } from './AuthContext';
import { SocketContextType } from '../types';
import { env } from '@/utils/env';

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      initializeSocket();
    } else {
      disconnectSocket();
    }

    return () => {
      disconnectSocket();
    };
  }, [user]);

  const initializeSocket = () => {
    const token = localStorage.getItem('sysup_token');
    
    if (!token) return;

    const socketUrl = env.SOCKET_URL;
    
    const newSocket = io(socketUrl, {
      auth: {
        token,
      },
      transports: ['websocket', 'polling'],
    });

    newSocket.on('connect', () => {
      console.log('✅ Socket.IO conectado');
      setConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('❌ Socket.IO desconectado');
      setConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('❌ Erro de conexão Socket.IO:', error);
      setConnected(false);
    });

    // Eventos globais
    newSocket.on('admin:notification', (data) => {
      toast.info(data.message, {
        autoClose: 8000,
      });
    });

    newSocket.on('user:status_changed', (data) => {
      toast.warning(`Status alterado: ${data.message}`, {
        autoClose: 10000,
      });
    });

    // Eventos de tickets
    newSocket.on('ticket:updated', (data) => {
      toast.info(`Chamado #${data.ticketId} atualizado: ${data.message}`, {
        autoClose: 8000,
      });
    });

    newSocket.on('support:ticket_notification', (data) => {
      toast.info(`Novo chamado: ${data.action}`, {
        autoClose: 8000,
      });
    });

    // Eventos financeiros
    newSocket.on('payment:confirmed', (data) => {
      toast.success(`Pagamento confirmado! Valor: R$ ${data.amount}`, {
        autoClose: 8000,
      });
    });

    newSocket.on('invoice:new', (data) => {
      toast.warning(`Nova fatura gerada! Vence em: ${new Date(data.dueDate).toLocaleDateString()}`, {
        autoClose: 10000,
      });
    });

    newSocket.on('financial:dashboard_update', (data) => {
      // Evento para atualizar dashboard em tempo real
      console.log('Dashboard atualizado:', data);
    });

    setSocket(newSocket);
  };

  const disconnectSocket = () => {
    if (socket) {
      socket.disconnect();
      setSocket(null);
      setConnected(false);
    }
  };

  const emit = (event: string, data?: any) => {
    if (socket && connected) {
      socket.emit(event, data);
    } else {
      console.warn('Socket não conectado. Evento não enviado:', event);
    }
  };

  const on = (event: string, callback: (data: any) => void) => {
    if (socket) {
      socket.on(event, callback);
    }
  };

  const off = (event: string) => {
    if (socket) {
      socket.off(event);
    }
  };

  const value: SocketContextType = {
    socket,
    connected,
    emit,
    on,
    off,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket deve ser usado dentro de um SocketProvider');
  }
  return context;
};

// Hook para facilitar notificações
export const useNotifications = () => {
  const { emit, on, off } = useSocket();

  const sendNotification = (type: 'admin' | 'support' | 'financial', data: any) => {
    emit(`${type}:notification`, data);
  };

  const subscribeToNotifications = (callback: (data: any) => void) => {
    on('notification', callback);
    return () => off('notification');
  };

  return {
    sendNotification,
    subscribeToNotifications,
  };
}; 